import _ from 'lodash'
import i18n from 'scoreboard/components/i18n'
import constants from 'scoreboard/components/constants'
import config from 'scoreboard/components/config'
import initiativeCalculationTypes from 'scoreboard/initiatives/initiative-calculation-types'
import ScorecardNode from 'scoreboard/scorecards/scorecard-node'

const THRESHOLD_PREFIX = 'threshold'
const GOAL_LABEL = 'scorecards.node.metric.threshold.goal'
/**
 * Returns all valid dataProperty dropdown options for the given scorecard or
 * initiative item.
 *
 * @param {Boolean} [options.includeGoalAlias=false] If this flag is set and the
 * scoring threshold matches the goalIndex AND none of the other thresholds
 * are "Goal" an additional option will be included labeled "Goal"
 */
function options (options = {}) {

	const { domainObject, domainType,
		includeGoalAlias = false, plotType } = options

	if (!domainObject) {
		return []
	}

	if (domainType === 'scorecard-item') {
		const {scoringTypeId} = domainObject
		let scoringType = config.scoringType(scoringTypeId)

		if (_.get(scoringType, 'standardScoringType') === constants.UNSCORED) {
			return [ actualValue() ]
		}

		// #46641 allow both score and value for scored text series
		if (_.get(scoringType, 'kpiValueType') === constants.SCORED_TEXT) {
			const options = [ score() ]

			// #50136 don't allow actual value for certain plot types
			if (!_.includes(['filled-container', 'waffle', 'funnel'],
				plotType)) {
				options.push(actualValue())
			}
			return options
		}

		var options = []

		if (config.scoresVisible) {
			options.push(score())
		}

		if (ScorecardNode.metricish(domainObject)) {
			options.push(actualValue())

			var type = config.scoringType(scoringTypeId)
			const thresholdNames = type.thresholdNames
			if (thresholdNames) {
				options = addMetricThresholdOptions(options,
					thresholdNames, scoringTypeId, type, includeGoalAlias)
			}
		}

		return options
	} else if (domainType === 'initiative') {
		return _(initiativeCalculationTypes.nonColorTypes()).map(type => {
			return {
				id: type.type,
				label: i18n.get(type.messageCode)
			}
		}).sortBy(type => type.label.toLocaleUpperCase()).value()
	} else {
		throw new Error(domainType + ' dataProperty options not implemented yet')
	}
 }

function joinThresholdId (index, scoringTypeId) {
	// The scoringTypeId is included here for when chart's reference lines
	// and bands features have a scorecard datasource, and the chart has
	// several scorecard serie with different scoring types.  Since the
	// threshold indexes are not distinct across scoring types, the select
	// will make multiple selections based on the option values being equal.
	return `${THRESHOLD_PREFIX}${index}_${scoringTypeId}`
}

function addMetricThresholdOptions (options, thresholdNames, scoringTypeId, type, includeGoalAlias) {
	var hasGoal = false
	options = options.concat(thresholdNames.map((key, i) => {
		hasGoal = hasGoal || thresholdNames[i] === GOAL_LABEL
		return {
			id: joinThresholdId(i, scoringTypeId),
			label: key
		}
	}))
	if (type.goalIndex && includeGoalAlias && !hasGoal) {
		// This is for a Line's Data Source, where we include "Goal"
		// options when they are absent but there is a goalIndex
		options.push({
			id: `${THRESHOLD_PREFIX}${type.goalIndex}_alias`,
			label: GOAL_LABEL
		})
	}
	return options
}

function actualValue () {
	return { id: 'value', label: 'scorecards.node.metric.actualValue' }
}

function score () {
	return { id: 'score', label: 'scorecards.node.score' }
}

/**
 * Given a dataProperty value that may have a threshold index it, returns an
 * object with a generic dataProperty and thresholdIndex. Examples:
 *
 * 'threshold1' --> { dataProperty: 'threshold', thresholdIndex: 1 }
 * 'score' --> { dataProperty: 'score' }
 * null --> {}
 */
function splitDataPropertyOption (option) {
	if (!option) {
		return {}
	}

	if (option.startsWith('threshold')) {
		const [threshold, scoringTypeId] = _.split(option, '_')
		return {
			dataProperty: THRESHOLD_PREFIX,
			thresholdIndex: parseInt(threshold.substring(`${THRESHOLD_PREFIX}`.length))
		}
	} else {
		return { dataProperty: option }
	}
}

/**
 * Combines the dataProperty and thresholdIndex (if applicable) into a single
 * value to be used in dropdowns. This is the reverse of splitDataPropertyOption();
 * see that method's documentation for examples.
 */
function joinDataPropertyOption (dataSource) {
	if (!_.get(dataSource, 'dataProperty')) {
		return null
	}

	return dataSource.dataProperty.startsWith(THRESHOLD_PREFIX)
		? `${THRESHOLD_PREFIX}${dataSource.thresholdIndex}`
		: dataSource.dataProperty
}

/**
 * Returns the default dataProperty value that should be used when initializing
 * the input or updating it after changing domain objects.
 *
 * @param {DomainType} domainType - the domain type of domainObject
 *
 * @param {Object} [domainObject] - the domain object (scorecard item or intiative)
 * the dataProperty is associated with
 *
 * @param {Array} selectOptions - the dropdown options that are valid for domainObject
 *
 * @param {Object} [previousSelectedOption] - when switching domain objects, the
 * dataProperty option that was previously selected
 *
 * @return {String} the value that should be initially selected. Will be null if
 * domainObject is null
 */
function defaultValue ({ domainType, domainObject, selectOptions, previousSelectedOption }) {
	if (!domainObject) {
		return null
	}

	if (domainType === 'scorecard-item') {
		return defaultScorecardProperty({
			scorecardItem: domainObject,
			selectOptions,
			previousSelectedOption
		})
	} else if (domainType === 'initiative') {
		// All initiatives have the same options, so just use the previously
		// selected value (if present)
		return _.get(previousSelectedOption, 'id')
	}
}

function defaultScorecardProperty ({ scorecardItem, selectOptions, previousSelectedOption}) {
	let metricish = ScorecardNode.metricish(scorecardItem)

	let defaultValue
	if (previousSelectedOption) {
		let previousValue = previousSelectedOption.id
		let previousLabel = previousSelectedOption.label

		if (metricish && previousValue === 'score') {
			// #16409 if user just picked a metric, and the previous dataProperty
			// was Score, switch it to Actual Value
			defaultValue = 'value'
		} else {
			// If user had previously picked a data property, keep it if possible.
			// The IDs actually can change for the thresholds (eg Goal is "1" for
			// Goal/RedFlag, but "0" for Goal Only), so match on label instead
			let matching = _.find(selectOptions, opt => i18n.get(opt.label) === previousLabel)
			if (matching) {
				defaultValue = matching.id
			}
		}
	}

	if (defaultValue) {
		return defaultValue
	}

	// If after all that, we still haven't found a good data field,
	// just pick Actual Value for metrics and Score for anything else
	return metricish ? 'value' : 'score'
}

export { options }
export { defaultValue }
export { splitDataPropertyOption }
export { joinDataPropertyOption }
export { joinThresholdId }
